import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  ArrowRight,
  Radar,
  Search,
  Workflow,
  Activity,
  Sparkles,
  TrendingUp,
  Target,
  ShieldCheck,
} from "lucide-react";
import { AuroraBackground } from "@/components/aurora-background";
import { GlassOrb } from "@/components/glass-orb";
import { GlassIcon } from "@/components/glass-icon";
import { SectionEyebrow } from "@/components/section-eyebrow";
import { MetricCounters } from "@/components/metric-counters";
import { OrbitLoop } from "@/components/orbit-loop";
import { TestimonialsMasonry } from "@/components/testimonials-masonry";
import { LandingHeader } from "@/components/landing-header";
import { TrustStrip } from "@/components/trust-strip";
import { ProblemSplit } from "@/components/problem-split";
import { Input } from "@/components/ui/input";
import { seo } from "@/lib/seo";
import { breadcrumb, webPage } from "@/lib/structured-data";
import { SITE_URL } from "@/lib/seo-constants";
import { signUpUrl, scrollToSection } from "@/lib/cta";

const LP_PATH = "/lp/replace-agencies-SMB";
const LP_TITLE = "Stop Bleeding Your Profit Margins on Expensive Marketing Agencies";
const LP_DESCRIPTION =
  "Run your entire SEO, GEO, and social media marketing on an AI engine that operates at a fraction of the cost and 10x the speed — without the $10,000/month agency retainer.";

export const Route = createFileRoute("/lp/replace-agencies-SMB")({
  head: () =>
    seo({
      title: LP_TITLE,
      description: LP_DESCRIPTION,
      path: LP_PATH,
      type: "website",
      jsonLd: [
        webPage({
          name: LP_TITLE,
          description: LP_DESCRIPTION,
          url: `${SITE_URL}${LP_PATH}`,
        }),
        breadcrumb([
          { name: "Home", url: "/" },
          { name: LP_TITLE, url: LP_PATH },
        ]),
      ],
    }),
  component: LandingPage,
});

const HERO_SIGNUP = signUpUrl("lp-replace-agencies-smb-hero");
const FINAL_SIGNUP = signUpUrl("lp-replace-agencies-smb-final");
const HEADER_SIGNUP = signUpUrl("lp-replace-agencies-smb-header");

const PILLARS = [
  {
    label: "Live Market Trends",
    description:
      "While traditional agencies take weeks to analyze quarterly trends, Axy.digital monitors your specific industry 24/7. It tracks exactly which keywords, topics, and customer questions are driving online sales right now, keeping you ahead of local and digital competitors.",
    icon: TrendingUp,
  },
  {
    label: "Real-Time Customer Intent",
    description:
      "Axy.digital maps exactly what your buyers are searching for across standard Google queries and modern AI chats (ChatGPT, Perplexity, Gemini). The system adapts your content instantly, meaning your business shows up exactly where buyers are looking today.",
    icon: Target,
  },
  {
    label: "Zero Ghostwritten \"Slop\"",
    description:
      "Most low-cost agencies outsource your work to cheap writers who use generic templates. Axy.digital deeply integrates your exact business profiles, unique services, and actual location parameters to build high-quality marketing assets that actually sound like your business.",
    icon: ShieldCheck,
  },
];

const FINAL_MATRIX = [
  {
    label: "Execution Velocity",
    value: "10× Faster Deployment",
    proof: "Continuous 24/7 Market Scans",
  },
  {
    label: "Financial Overhead",
    value: "Fraction of Agency Cost",
    proof: "Zero Monthly Retainer Fees",
  },
  {
    label: "Onboarding Window",
    value: "Ready in 120 Seconds",
    proof: "Zero Setup or Prompt Workflows",
  },
];

function AgencyAuditForm() {
  const [domain, setDomain] = useState("");
  const [email, setEmail] = useState("");

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const url = new URL(signUpUrl("lp-replace-agencies-smb-agency-audit"));
    if (domain) url.searchParams.set("domain", domain);
    if (email) url.searchParams.set("email", email);
    window.location.href = url.toString();
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-3 w-full">
      <Input
        type="text"
        required
        placeholder="yourcompany.com"
        value={domain}
        onChange={(e) => setDomain(e.target.value)}
        className="h-12 bg-white/5 border-white/15 text-white placeholder:text-white/40 flex-1"
      />
      <Input
        type="email"
        required
        placeholder="business@email.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="h-12 bg-white/5 border-white/15 text-white placeholder:text-white/40 flex-1"
      />
      <button
        type="submit"
        className="btn-gradient hover:brightness-110 transition-all rounded-full px-6 h-12 text-sm font-medium inline-flex items-center justify-center gap-2 whitespace-nowrap"
      >
        Audit My Current Agency's Strategy
        <ArrowRight className="size-4" />
      </button>
    </form>
  );
}

function LandingPage() {
  return (
    <>
      <LandingHeader ctaLabel="Replace agency with Axy.digital" ctaHref={HEADER_SIGNUP} />

      {/* HERO */}
      <section className="relative px-6 pt-20 pb-24 overflow-hidden">
        <AuroraBackground />
        <div
          aria-hidden
          className="absolute left-1/2 top-24 -translate-x-1/2 opacity-60 pointer-events-none"
        >
          <GlassOrb size="lg" />
        </div>
        <div className="relative max-w-4xl mx-auto text-center">
          <SectionEyebrow className="justify-center text-center">
            The weekly output of a full agency, without the $10K/month retainer
          </SectionEyebrow>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-semibold tracking-tight text-white text-balance mb-6">
            Stop Bleeding Your Profit Margins on Expensive Marketing Agencies.
          </h1>
          <p className="text-lg md:text-xl text-white/75 max-w-3xl mx-auto mb-10 text-pretty">
            Run your entire SEO, GEO, and social media marketing on an AI marketing engine that
            operates at a fraction of the cost and 10x the speed. Get the weekly marketing output
            of a full agency, without the $10,000/month retainer.
          </p>
          <div className="flex flex-col items-center gap-3">
            <a
              href={HERO_SIGNUP}
              className="btn-gradient hover:-translate-y-px hover:brightness-110 transition-all rounded-full px-6 py-3 text-sm md:text-base font-medium inline-flex items-center gap-2"
            >
              Replace agency with Axy.digital (Free · 2 Mins)
              <ArrowRight className="size-4" />
            </a>
            <p className="text-xs text-white/55">
              Engine live • 1,500+ businesses • No credit card required
            </p>
          </div>
        </div>
      </section>

      <TrustStrip />

      {/* METRICS BANNER */}
      <section className="px-6 py-20">
        <div className="max-w-6xl mx-auto">
          <SectionEyebrow>Protect your profit margin & automate marketing</SectionEyebrow>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white text-balance mb-3 max-w-3xl">
            A full marketing team's output — for less than a single freelancer's invoice.
          </h2>
          <p className="text-white/65 max-w-2xl mb-10">
            Built for SMB owners who refuse to keep funneling profit into bloated retainers, slow
            sprints, and generic ghostwritten drafts.
          </p>
          <MetricCounters
            items={[
              {
                value: 60,
                prefix: "+",
                suffix: " hrs",
                label: "Saved monthly",
                proof: "Research, writing & publishing automated",
              },
              {
                value: 99,
                suffix: "%",
                label: "Retainer savings",
                proof: "Vs. a $10K/month agency",
              },
              {
                value: 11,
                suffix: "×",
                label: "AI visibility growth",
                proof: "Across 90-day rollouts",
              },
              {
                value: 40,
                prefix: "+",
                suffix: "%",
                label: "Referral traffic",
                proof: "Within 90 days of deployment",
              },
            ]}
          />
        </div>
      </section>

      {/* PROBLEM */}
      <section className="px-6 py-24">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-12">
            <SectionEyebrow>The Problem</SectionEyebrow>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white text-balance">
              Your market moves daily. Your{" "}
              <span className="text-gradient">agency doesn't.</span>
            </h2>
            <p className="mt-4 text-lg text-white/70 max-w-2xl">
              Bloated retainers, slow sprint cycles, generic drafts, and quarterly trend reports.
              Marketing agencies aren't built for the speed — or the budget — of a real SMB.
            </p>
          </div>
          <ProblemSplit />
        </div>
      </section>

      {/* 3 PILLARS */}
      <section className="relative px-6 py-24 overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-60"
          style={{
            background:
              "radial-gradient(60% 40% at 30% 20%, rgba(123,63,242,0.18), transparent 60%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto">
          <SectionEyebrow>How to protect your profit margin & automate marketing</SectionEyebrow>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white text-balance mb-6 max-w-3xl">
            The agency experience, powered by AI agents.
          </h2>
          <p className="text-white/75 leading-relaxed text-pretty max-w-3xl mb-12">
            Three always-on agents replace the strategists, analysts, and writers your agency
            used to bill you thousands for every month.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PILLARS.map((p, i) => (
              <div
                key={p.label}
                className="glass-card p-6 hover-lift hover:-translate-y-1 relative"
              >
                <div className="flex items-center justify-between mb-4">
                  <GlassIcon icon={p.icon} />
                  <span className="text-xs uppercase tracking-wider text-white/40 tabular-nums">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="text-base font-semibold text-white mb-2">{p.label}</h3>
                <p className="text-sm text-white/65 leading-relaxed">{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ORBIT LOOP */}
      <section className="px-6 py-24">
        <div className="max-w-6xl mx-auto text-center mb-12">
          <SectionEyebrow className="justify-center">
            Get the output of a full marketing agency, fully automated
          </SectionEyebrow>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white text-balance max-w-3xl mx-auto mb-4">
            A continuous growth cycle across 5 simple phases.
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto">
            Axy.digital handles all the complex research, writing, and publishing you used to pay an agency
            thousands of dollars to manage — without the human overhead.
          </p>
        </div>
        <div className="max-w-5xl mx-auto">
          <OrbitLoop
            steps={[
              {
                label: "Find What Buyers Want",
                description:
                  "Axy.digital tracks local and digital market shifts 24/7 to pinpoint the exact questions, services, and trends your customers are actively searching for online right now.",
                icon: Radar,
              },
              {
                label: "Spot Your Traffic Leaks",
                description:
                  "Instantly see exactly where your website is completely invisible and identify which local or digital competitors are currently stealing your customers and search traffic.",
                icon: Search,
              },
              {
                label: "Launch Hands-Free Marketing",
                description:
                  "The system automatically writes and deploys premium website content, business pages, and social posts tailored perfectly to your business — no agencies or writing required.",
                icon: Workflow,
              },
              {
                label: "Track Your Real Growth",
                description:
                  "Ditch the confusing, bloated agency spreadsheets. Maintain absolute clarity with a clean dashboard that shows your customer visibility growing over time.",
                icon: Activity,
              },
              {
                label: "Automatic Optimization",
                description:
                  "Run an always-on marketing machine that automatically tweaks and updates your content based on new Google updates, weekly search trends, and what is bringing in actual traffic.",
                icon: Sparkles,
              },
            ]}
          />
          <div className="mt-8 flex justify-center">
            <span className="glass-pill px-4 py-2 text-xs text-white/85 inline-flex items-center gap-2">
              <span className="size-1.5 rounded-full bg-pink-soft animate-pulse" />0 manual steps ·
              Loop running on autopilot
            </span>
          </div>
        </div>
      </section>

      {/* AGENCY AUDIT — SECONDARY CTA */}
      <section id="agency-audit-zone" className="px-6 py-20 scroll-mt-20">
        <div className="max-w-4xl mx-auto">
          <div className="glass-card p-8 md:p-12">
            <SectionEyebrow>Free agency audit</SectionEyebrow>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white text-balance mb-4">
              🕵️‍♂️ Want to see what your current marketing agency is missing?
            </h2>
            <p className="text-white/70 max-w-2xl mb-8 text-pretty">
              Stop paying for bloated retainers just to get average results. Enter your company
              domain below and Axy.digital will run a comprehensive audit to uncover the exact AI search
              citation gaps and social trend blindspots your current team hasn't fixed.
            </p>
            <AgencyAuditForm />
            <p className="mt-4 text-xs text-white/50">
              We'll email your agency strategy audit within minutes.
            </p>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <TestimonialsMasonry
        eyebrow="Built to level the playing field for SMBs"
        heading="SMBs replacing agencies with Axy.digital."
        items={[
          {
            quote:
              "Axy.digital helped us 5x our content output without sounding generic. The content is detailed, sharp, and actually reflects how we want to show up as an early-stage team.",
            name: "Aurelien Vasinis",
            role: "Founder & CEO",
            company: "Kuration AI",
            initials: "AV",
            hue: 295,
            metric: { value: "60+ hrs", label: "saved monthly" },
            featured: true,
          },
          {
            quote:
              "In 90 days, Axy.digital helped us go from invisible in AI answers to ranking #3 against incumbents with a decade of SEO dominance.",
            name: "Savannah Thatcher",
            role: "CMO",
            company: "Wingbits",
            initials: "ST",
            hue: 320,
            metric: { value: "11×", label: "AI visibility growth" },
          },
          {
            quote:
              "The depth of the content Axy.digital generated from very little input was genuinely impressive. Not having to prompt or configure everything manually was a game changer.",
            name: "Florian Rennberger",
            role: "Head of Sales",
            company: "Conducttr Germany",
            initials: "FR",
            hue: 270,
          },
        ]}
      />

      {/* FINAL CTA */}
      <section className="relative px-6 py-24 border-t border-neutral-800 bg-neutral-950 overflow-hidden">
        <div className="relative max-w-5xl mx-auto text-center">
          <div className="flex justify-center mb-8">
            <span className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full border border-emerald-400/25 bg-emerald-400/5 text-[11px] uppercase tracking-[0.18em] text-emerald-300/90 font-mono">
              <span className="relative flex size-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex size-2 rounded-full bg-emerald-400" />
              </span>
              Engine Status: Ready to Deploy
            </span>
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white text-balance mb-5">
            Take Control of Your Runway.
          </h2>
          <p className="text-lg text-white/70 max-w-2xl mx-auto mb-12 text-pretty">
            Stop paying for bloated agency retainers, scheduling alignment calls, and reviewing
            slow, generic drafts. Turn your marketing into an automated, high-velocity distribution
            factory.
          </p>

          {/* 3-column capital-efficiency matrix */}
          <div className="grid grid-cols-1 md:grid-cols-3 border border-white/10 rounded-2xl overflow-hidden mb-12 bg-white/[0.02]">
            {FINAL_MATRIX.map((m, i) => (
              <div
                key={m.label}
                className={[
                  "px-5 py-6 text-left",
                  i < FINAL_MATRIX.length - 1 ? "md:border-r border-white/10" : "",
                  i < FINAL_MATRIX.length - 1 ? "border-b md:border-b-0" : "",
                ].join(" ")}
              >
                <div className="text-xs uppercase tracking-wider text-white/45 mb-2">
                  {m.label}
                </div>
                <div className="text-2xl md:text-3xl font-semibold text-white mb-1">
                  {m.value}
                </div>
                <div className="text-xs text-neutral-400">{m.proof}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-col items-center gap-3">
            <a
              href={FINAL_SIGNUP}
              className="btn-gradient hover:-translate-y-px hover:brightness-110 transition-all rounded-full px-7 py-3.5 text-sm md:text-base font-medium inline-flex items-center gap-2"
            >
              Replace Your Agency Overhead (Free · 2 Mins)
              <ArrowRight className="size-4" />
            </a>
            <p className="text-xs text-white/55">
              Free setup includes AI visibility report and live market signal maps. Cancel your
              agency dependencies today.
            </p>
          </div>

          <div className="mt-10 text-sm text-white/55">
            Want to see what your current agency is missing first?{" "}
            <button
              type="button"
              onClick={() => scrollToSection("agency-audit-zone")}
              className="text-white/85 underline underline-offset-4 hover:text-white transition-colors"
            >
              Run Free Agency Audit
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
